import { useState } from 'react';
import type { JourneyGraph, JourneyStepDto } from '@ederest/contracts';
import { Badge } from '../../../shared/ui/Badge.js';
import { Card } from '../../../shared/ui/Card.js';
import { FrictionScore, NOTABLE_EXIT_RATE } from '../../../shared/ui/FrictionScore.js';
import { Table, type Column } from '../../../shared/ui/Table.js';
import { JourneyFlow } from './JourneyFlow.js';
import { JourneyLineage } from './JourneyLineage.js';
import './journey-reading.css';

interface JourneyReadingProps {
  graph: JourneyGraph;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}

const count = (value: number) => value.toLocaleString('fr-FR');
const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
/** A page name in Arabic must not reorder the French sentence around it (first-strong isolate). */
const isolate = (text: string) => `⁨${text}⁩`;

interface StepReading {
  step: JourneyStepDto;
  /** Sessions that moved on to a later step, summed over the observed transitions. */
  continued: number;
  exited: number;
  proposed: number;
  live: number;
}

/**
 * The journey in words and in a table, derived from the graph the API returns:
 * who entered each page, who went on, who left, where the friction sits and
 * what guidance exists. The canvas stays for exploration; this is what a reader
 * takes in first.
 */
export function JourneyReading({ graph, selectedStepId, onSelectStep }: JourneyReadingProps) {
  const [view, setView] = useState<'flow' | 'graph'>('flow');
  const steps = [...graph.steps].sort((a, b) => a.position - b.position);
  const readings = steps.map((step) => readingOf(step, graph));

  const hardest = [...readings].sort((a, b) => b.step.frictionScore - a.step.frictionScore)[0];
  const proposed = readings.reduce((sum, reading) => sum + reading.proposed, 0);
  const live = readings.reduce((sum, reading) => sum + reading.live, 0);

  const columns: Array<Column<StepReading>> = [
    {
      key: 'page',
      header: 'Page',
      render: (row) => (
        <div className="reading__page">
          <span className="reading__index">{row.step.position}</span>
          <div>
            <div className="cell-primary__label">{row.step.name}</div>
            <div className="cell-primary__sub reading__path">{row.step.pagePathPattern}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'entered',
      header: 'Usagers entrés',
      align: 'end',
      render: (row) => count(row.step.sessionCount),
    },
    {
      key: 'continued',
      header: 'Ont continué',
      align: 'end',
      render: (row) => (row.continued === 0 ? <span className="cell-muted">—</span> : count(row.continued)),
    },
    {
      key: 'exited',
      header: 'Ont quitté',
      align: 'end',
      render: (row) =>
        row.exited === 0 ? (
          <span className="cell-muted">—</span>
        ) : (
          <span className={row.step.dropOffRate >= NOTABLE_EXIT_RATE ? 'reading__exit--hot' : undefined}>
            {count(row.exited)} · {percent(row.step.dropOffRate)}
          </span>
        ),
    },
    {
      key: 'friction',
      header: 'Friction /100',
      render: (row) => <FrictionScore score={row.step.frictionScore} />,
    },
    {
      key: 'guidance',
      header: 'Guidance',
      render: (row) => (
        <span className="reading__chips">
          {row.live > 0 && <Badge tone="positive">{row.live} en ligne</Badge>}
          {row.proposed > 0 && (
            <Badge tone="brand">
              {row.proposed} proposée{row.proposed > 1 ? 's' : ''}
            </Badge>
          )}
          {row.live === 0 && row.proposed === 0 && <span className="cell-muted">—</span>}
        </span>
      ),
    },
  ];

  return (
    <div className="reading" data-testid="journey-reading">
      <Card
        title="Lecture du parcours"
        actions={
          <div className="reading__views" role="group" aria-label="Vue du parcours">
            <button
              type="button"
              className={view === 'flow' ? 'reading__view reading__view--active' : 'reading__view'}
              aria-pressed={view === 'flow'}
              onClick={() => setView('flow')}
            >
              Flux
            </button>
            <button
              type="button"
              className={view === 'graph' ? 'reading__view reading__view--active' : 'reading__view'}
              aria-pressed={view === 'graph'}
              onClick={() => setView('graph')}
            >
              Vue graphe
            </button>
          </div>
        }
      >
        {view === 'flow' ? (
          <JourneyFlow graph={graph} selectedStepId={selectedStepId} onSelectStep={onSelectStep} />
        ) : (
          <JourneyLineage graph={graph} selectedStepId={selectedStepId} onSelectStep={onSelectStep} />
        )}
        <footer className="reading__facts">
          {hardest && hardest.step.frictionScore > 0 && (
            <span>
              Plus forte friction : « {isolate(hardest.step.name)} »{' '}
              <FrictionScore score={hardest.step.frictionScore} />
            </span>
          )}
          <span>
            Guidances :{' '}
            {live === 0 && proposed === 0 ? (
              'aucune proposée ni en ligne'
            ) : (
              <>
                {live > 0 && (
                  <>
                    <strong>{count(live)}</strong> en ligne
                  </>
                )}
                {live > 0 && proposed > 0 && ' · '}
                {proposed > 0 && (
                  <>
                    <strong>{count(proposed)}</strong> en attente de décision
                  </>
                )}
              </>
            )}
          </span>
        </footer>
      </Card>

      <section className="reading__table" aria-label="Pages du parcours">
        <Table
          columns={columns}
          rows={readings}
          rowKey={(row) => row.step.id}
          onRowClick={(row) => onSelectStep(row.step.id)}
          empty="Aucune page mesurée."
        />
        {selectedStepId === null && readings.length > 0 && (
          <p className="reading__hint">
            Cliquez sur une page pour lire sa mesure et décider de ses guidances.
          </p>
        )}
      </section>
    </div>
  );
}

function readingOf(step: JourneyStepDto, graph: JourneyGraph): StepReading {
  const continued = graph.transitions
    .filter((transition) => transition.fromStepId === step.id)
    .reduce((sum, transition) => sum + transition.sessionCount, 0);
  const proposed = graph.guidances.filter(
    (guidance) =>
      guidance.stepId === step.id && (guidance.state === 'proposed' || guidance.state === 'approved'),
  ).length;
  const live = graph.guidances.filter(
    (guidance) => guidance.stepId === step.id && guidance.state === 'published',
  ).length;
  return {
    step,
    continued,
    exited: Math.round(step.sessionCount * step.dropOffRate),
    proposed,
    live,
  };
}
